<template>
  <div class="con-upload">
    <Teleport to="body">
      <transition name="view-upload">
        <div v-if="viewActive" class="view-upload" @click="closeView">
          <img :src="viewSrc" alt="image" />
        </div>
      </transition>
    </Teleport>

    <div class="con-img-upload">
      <div
        v-for="(file, index) in visibleFiles"
        :key="file.id"
        class="img-upload"
        :class="{
          fileError: file.error,
          removeItem: file.removing,
        }"
      >
        <button type="button" class="btn-x-file" @click="removeFile(file)">
          <i class="material-icons notranslate" translate="no">clear</i>
        </button>
        <button
          v-if="showUploadButton"
          type="button"
          class="btn-upload-file"
          :class="{
            'on-progress': file.percent,
            'ready-progress': file.percent >= 100,
          }"
          :style="{ height: `${file.percent || 0}%` }"
          @click="upload(index)"
        >
          <i class="material-icons notranslate" translate="no">
            {{
              file.percent >= 100
                ? file.error
                  ? "report_problem"
                  : "cloud_done"
                : "cloud_upload"
            }}
          </i>
          <span>{{ file.percent }} %</span>
        </button>
        <img
          v-if="file.src"
          :src="file.src"
          :style="{
            maxWidth: file.orientation === 'h' ? '100%' : 'none',
            maxHeight: file.orientation === 'w' ? '100%' : 'none',
          }"
          @click="viewImage(file.src)"
        />
        <h4 v-else class="text-archive">
          <i class="material-icons notranslate" translate="no">description</i>
          <span>{{ file.name }}</span>
        </h4>
      </div>

      <div
        class="con-input-upload"
        :class="{
          'on-progress-all-upload': percent !== 0,
          'is-ready-all-upload': percent >= 100,
          'disabled-upload': uploadDisabled,
        }"
      >
        <input
          v-bind="inputAttrs"
          ref="fileInput"
          type="file"
          :disabled="uploadDisabled"
          @change="getFiles"
        />
        <span class="text-input">{{ uploadDisabled ? textMax : text }}</span>
        <span class="input-progress" :style="{ width: `${percent}%` }" />
        <button
          v-if="showUploadButton"
          type="button"
          title="Upload"
          class="btn-upload-all vs-upload--button-upload"
          :disabled="pendingFiles.length === 0"
          @click="upload('all')"
        >
          <i class="material-icons notranslate" translate="no">cloud_upload</i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
let fileId = 0;

export default {
  name: "VsUpload",
  inheritAttrs: false,
  props: {
    fileName: {
      type: String,
      default: null,
    },
    text: {
      type: String,
      default: "Upload File",
    },
    textMax: {
      type: String,
      default: "Maximum of files reached",
    },
    limit: {
      type: [Number, String],
      default: null,
    },
    action: {
      type: String,
      default: null,
    },
    headers: {
      type: Object,
      default: null,
    },
    data: {
      type: Object,
      default: null,
    },
    automatic: {
      type: Boolean,
      default: false,
    },
    showUploadButton: {
      type: Boolean,
      default: true,
    },
    singleUpload: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:vsFile", "change", "on-delete", "on-error", "on-success"],
  data() {
    return {
      files: [],
      percent: 0,
      viewActive: false,
      viewSrc: null,
    };
  },
  computed: {
    inputAttrs() {
      const attrs = { ...this.$attrs };
      ["class", "style", "onChange"].forEach((key) => delete attrs[key]);
      return attrs;
    },
    visibleFiles() {
      return this.files.filter((file) => !file.removed);
    },
    pendingFiles() {
      return this.files.filter(
        (file) => !file.removed && !file.success
      );
    },
    uploadDisabled() {
      if (
        this.$attrs.disabled === "" ||
        this.$attrs.disabled === true ||
        this.$attrs.disabled === "disabled"
      ) {
        return true;
      }
      return this.limit
        ? this.visibleFiles.length >= Number(this.limit)
        : false;
    },
  },
  methods: {
    viewImage(src) {
      this.viewSrc = src;
      this.viewActive = true;
    },
    closeView(event) {
      if (event.target.tagName !== "IMG") this.viewActive = false;
    },
    createPreview(file) {
      const preview = {
        id: ++fileId,
        file,
        name: file.name,
        src: null,
        orientation: "h",
        percent: null,
        error: false,
        removed: false,
        removing: false,
        success: false,
      };

      if (/image.*/.test(file.type)) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const image = new Image();
          image.onload = () => {
            preview.orientation =
              image.width > image.height ? "w" : "h";
          };
          image.src = event.target.result;
          preview.src = event.target.result;
        };
        reader.readAsDataURL(file);
      }

      return preview;
    },
    getFiles(event) {
      const selected = Array.from(event.target.files || []);
      const remaining = this.limit
        ? Math.max(Number(this.limit) - this.visibleFiles.length, 0)
        : selected.length;
      const accepted = selected.slice(0, remaining);

      accepted.forEach((file) => {
        this.files.push(this.createPreview(file));
      });

      this.$emit("update:vsFile", event.target.value);
      this.$emit(
        "change",
        event.target.value,
        this.visibleFiles.map((item) => item.file)
      );

      if (this.automatic && accepted.length) this.upload("all");
    },
    removeFile(file) {
      file.removing = true;
      this.$emit("on-delete", file.file);
      setTimeout(() => {
        file.removed = true;
        file.removing = false;
        const input = this.$refs.fileInput;
        if (input) input.value = "";
      }, 301);
    },
    upload(index) {
      if (!this.action || !this.fileName) return;
      const files =
        typeof index === "number"
          ? [this.visibleFiles[index]]
          : this.pendingFiles;

      if (this.singleUpload) {
        files.forEach((file) => this.sendFiles([file], index));
      } else {
        this.sendFiles(files, index);
      }
    },
    sendFiles(files, index) {
      if (!files.length) return;
      const formData = new FormData();
      Object.entries(this.data || {}).forEach(([key, value]) => {
        formData.append(key, value);
      });
      files.forEach((file) => {
        formData.append(this.fileName, file.file, file.file.name);
      });

      const xhr = new XMLHttpRequest();
      xhr.withCredentials = true;
      xhr.onerror = (event) => {
        files.forEach((file) => {
          file.error = true;
        });
        this.$emit("on-error", event);
      };
      xhr.onload = (event) => {
        if (xhr.status < 200 || xhr.status >= 300) {
          files.forEach((file) => {
            file.error = true;
          });
          this.$emit("on-error", event);
          return;
        }
        files.forEach((file) => {
          file.success = true;
        });
        this.$emit("on-success", event);
      };
      if (xhr.upload) {
        xhr.upload.onprogress = (event) => {
          if (!event.total) return;
          const value = Math.trunc((event.loaded / event.total) * 100);
          if (typeof index === "number" && files[0]) {
            files[0].percent = value;
          } else {
            this.percent = value;
            files.forEach((file) => {
              file.percent = value;
            });
          }
        };
      }
      xhr.open("POST", this.action, true);
      Object.entries(this.headers || {}).forEach(([key, value]) => {
        if (value !== null) xhr.setRequestHeader(key, value);
      });
      xhr.send(formData);
    },
  },
};
</script>
